import { type ReactNode, useMemo } from "react";
import Datepicker, {
  type DateValueType,
} from "shared/components/common/datepicker/src";
import { usePillarTableFilterState } from "shared/components/pillar-table/filters/hooks/usePillarTableFilterState";
import PillarTableFilterLabel from "shared/components/pillar-table/filters/PillarTableFilterLabel";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { type SocietyView } from "shared/mappers/database/society/society";

type PillarTableFilterDatePickerProps = {
  filterKey: string;
  title: string;
  toolTip?: ReactNode;
  shortcuts?: {
    [key: string]:
      | { text: string; period: { start: string | null; end: string | null } }
      | string;
  };
  className?: string;
  society?: SocietyView;
};

const checkIfValueString = (value: unknown) =>
  typeof value === "string" ? value : undefined;

const PillarTableFilterDatePicker = ({
  filterKey,
  title,
  toolTip,
  shortcuts,
  society,
  className,
}: PillarTableFilterDatePickerProps) => {
  const { isPhoneMedia } = useCustomSession();
  const { setFilterValue, filters } = usePillarTableFilterState();

  const fromFilterKey = filterKey + "-from";
  const toFilterKey = filterKey + "-to";
  const fromFilterValue = checkIfValueString(filters[fromFilterKey]);
  const toFilterValue = checkIfValueString(filters[toFilterKey]);

  const datePickerValue = useMemo(
    () => ({
      startDate: fromFilterValue ?? null,
      endDate: toFilterValue ?? null,
    }),
    [fromFilterValue, toFilterValue],
  );

  //TODO : Add sa shared context hook similar to useCustomRouter that works on admin and hub

  const startingMonthBilling = parseInt(
    society?.societySettingsPublic?.fiscalYearStartDay ?? "1",
  );
  const startingDayBilling = parseInt(
    society?.societySettingsPublic?.fiscalYearStartMonth ?? "1",
  );

  const handleValueChange = (newValue: DateValueType) => {
    const datesFilter = {
      start: checkIfValueString(newValue?.startDate),
      end: checkIfValueString(newValue?.endDate),
    };
    if (
      fromFilterValue != datesFilter.start ||
      toFilterValue != datesFilter.end
    ) {
      setFilterValue((prev) => ({
        ...prev,
        [fromFilterKey]: datesFilter.start,
        [toFilterKey]: datesFilter.end,
      }));
    }
  };
  const overrideDefaultShortcuts = {
    today: "Today",
    yesterday: "Yesterday",
    yearToDate: "Year-to-Date",
    monthToDate: "Month-to-Date",
    previousWeek: "Previous 7 Days",
    nextWeek: "Next 7 Days",
    previousBillingPeriod: "Previous Billing Period",
    currentBillingPeriod: "Current Billing Period",
    nextBillingPeriod: "Next Billing Period",
  };
  return (
    <>
      <PillarTableFilterLabel
        className="text-neutral-mid-600"
        htmlFor={`filter-single-date-${filterKey}`}
        filterKey={filterKey}
        title={title}
        toolTip={toolTip}
      />
      <div>
        <Datepicker
          showShortcuts={true}
          showFooter={isPhoneMedia}
          popoverDirection="down"
          displayFormat="MM-DD-YYYY"
          configs={{
            shortcuts: shortcuts ?? overrideDefaultShortcuts,
            footer: {
              cancel: "Cancel",
              apply: "Apply",
            },
          }}
          value={datePickerValue}
          onChange={(e) => handleValueChange(e)}
          postfixID={filterKey}
          calendarContainerClass={`transition-all ease-out duration-300 fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 mt-[1px] text-sm lg:text-xs 2xl:text-sm opacity-0 hidden bg-neutral-light phone:inset-0 phone:translate-x-0 phone:translate-y-0 phone:overflow-auto ${className}`}
          startingTime={{
            startDay: startingDayBilling,
            startMonth: startingMonthBilling,
          }}
        />
      </div>
    </>
  );
};
export default PillarTableFilterDatePicker;
